// Exportação do painel "Saldos das UCs" — a planilha que substitui a aba "Revisão".

import { buildSheet, downloadWorkbook, fileStamp, type ColType } from '../../../src/lib/excelExport';
import { RateioAjusteUc } from '../../../types';
import { DIVERGENCIAS, STATUS_AJUSTE, TIPOS_AJUSTE, type LinhaSaldoUc, type ResumoGeradora } from './saldosUcs';
import { FAIXA_META, TENDENCIA_META } from './saldosUcsVisual';

/** Fração → número em % para a célula (0.0004 → 0.04), formatado com 2 casas. */
const pct = (f: number | null | undefined): number | string => (f == null ? '' : Math.round(f * 1000000) / 10000);
const n = (v: number | null | undefined): number | string => (v == null || !Number.isFinite(v) ? '' : Math.round(v * 100) / 100);

export function exportarSaldosUcs(
  linhas: LinhaSaldoUc[],
  resumo: ResumoGeradora[],
  ajustesPorLinha: (l: LinhaSaldoUc) => RateioAjusteUc[],
  mesRateio: string
) {
  const headers = [
    'UC', 'Nova UC', 'Nome', 'CPF/CNPJ', 'Status (rateio)', 'Status (cadastro)', 'UC Geradora', 'Usina',
    '% vigente', '% rateio anterior', '% sugerido', 'Diferença (p.p.)',
    'Consumo base (kWh)', 'Compensável (kWh)', 'Ajustado +15% (kWh)', 'Geração média usina (kWh)',
    'Última fatura', 'Crédito recebido (kWh)', 'Saldo (kWh)', 'Mês do saldo', 'Cobertura (meses)', 'Faixa', 'Tendência',
    'Divergências', 'Ajuste em aberto',
  ];
  const tipos: ColType[] = [
    'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt',
    'kwh', 'kwh', 'kwh', 'kwh',
    'kwh', 'kwh', 'kwh', 'kwh',
    'txt', 'kwh', 'kwh', 'txt', 'kwh', 'txt', 'txt',
    'txt', 'txt',
  ];
  const rows = linhas.map(l => {
    const abertos = ajustesPorLinha(l).filter(a => a.status === 'pendente' || a.status === 'enviado');
    return [
      l.ucNumero, l.ucNovo || '', l.nome, l.cpfCnpj, l.statusPlanilha, l.statusCadastro || '', l.ugNumero || '', l.ugNome || '',
      pct(l.percentVigente), pct(l.percentAnterior), pct(l.sugestao?.percent), pct(l.diferencaSugestao),
      n(l.sugestao?.consumoKwh ?? l.consumo.valorKwh), n(l.sugestao?.compensavelKwh), n(l.sugestao?.ajustadoKwh), n(l.sugestao?.geracaoMediaKwh),
      l.ultimaFatura?.mes || '', n(l.ultimaFatura?.creditoRecebidoKwh), n(l.saldo?.kwh), l.saldo?.mes || '', n(l.cobertura?.coberturaMeses),
      l.faixa ? FAIXA_META[l.faixa].rotulo : '', l.tendencia ? TENDENCIA_META[l.tendencia].rotulo : '',
      l.divergencias.map(d => DIVERGENCIAS[d].rotulo).join('; '),
      abertos.map(a => `${TIPOS_AJUSTE[a.tipo]} (${STATUS_AJUSTE[a.status]}, para ${a.mes_rateio_alvo || '—'})`).join('; '),
    ];
  });
  const planilhaUcs = buildSheet(headers, rows, tipos, { autofilter: true });

  const planilhaUsinas = buildSheet(
    ['UC Geradora', 'Usina', 'Geração média (kWh)', 'Meses na média', 'UCs recebendo', '% alocado (vigente)', 'Sobra (%)', '% sugerido (soma)', 'UCs sem sugestão'],
    resumo.map(r => [
      r.ugNumero, r.ugNome || '', n(r.mediaGeracaoKwh), r.mesesGeracao, r.ucsRecebendo,
      pct(r.somaVigente), pct(1 - r.somaVigente), pct(r.somaSugerida), r.semSugestao,
    ]),
    ['txt', 'txt', 'kwh', 'num', 'num', 'kwh', 'kwh', 'kwh', 'num']
  );

  downloadWorkbook(
    [
      { name: 'UCs', ws: planilhaUcs },
      { name: 'Usinas', ws: planilhaUsinas },
    ],
    `Saldos_UCs_${mesRateio.replace('/', '-')}_${fileStamp()}.xlsx`
  );
}
